import { Injectable } from '@angular/core';
import { LoggerService } from '../handlers/logger.service';
import { GridExtractionService } from './grid-extraction.service';

@Injectable()
export class StatementGridExtractionService extends GridExtractionService {
  // header keyword to detect
  protected override readonly HEADER_KEYWORDS = [
    'post. date', 'date', 'invoice date',
    'activity', 'type', 'trans type',
    'document bp ref. no.', 'invoice no.', 'reference', 'ref', 'transaction no.',
    'amount', 'payments'
  ];
  // header keyword used for auto-mapping logic
  private readonly DOCUMENT_DATE_HEADER_KEYWORDS = ['post. date', 'date', 'invoice date'];
  private readonly DOCUMENT_NUMBER_HEADER_KEYWORDS = ['document bp ref. no.', 'invoice no.', 'reference', 'ref', 'transaction no.'];
  private readonly AMOUNT_HEADER_KEYWORDS = ['amount', 'payments'];
  private readonly DOCUMENT_TYPE_KEYWORDS = ['activity', 'type', 'trans type'];

  constructor(logger: LoggerService) {
    super(logger);
  }

  override getAutoColumnMappings(
    columnSummaries: Array<{ index: number; header: string; dataCharacteristic?: string; averageCurrencyValue?: number | null }>,
    lineItemColumns: Array<{ field: string; header: string }>
  ): { [key: number]: string } | null {
    const availableFields = new Set((lineItemColumns || []).map(column => column.field));
    const mappings: { [key: number]: string } = {};
    const assignedFields = new Set<string>();
    const assignedColumns = new Set<number>();

    const assign = (field: string, summary?: { index: number }): void => {
      if (!summary || !availableFields.has(field) || assignedFields.has(field) || assignedColumns.has(summary.index)) {
        return;
      }

      mappings[summary.index] = field;
      assignedFields.add(field);
      assignedColumns.add(summary.index);
    };

    const findByHeader = (matcher: (header: string) => boolean) => {
      return columnSummaries.find(summary => !assignedColumns.has(summary.index) && matcher(this.normalizeHeader(summary.header)));
    };

    assign('documentDate', findByHeader(header =>
      this.headerContainsAnyKeyword(header, this.DOCUMENT_DATE_HEADER_KEYWORDS)
    ));
    assign('documentType', findByHeader(header =>
      this.headerContainsAnyKeyword(header, this.DOCUMENT_TYPE_KEYWORDS)
    ));
    assign('amount', findByHeader(header =>
      this.headerContainsAnyKeyword(header, this.AMOUNT_HEADER_KEYWORDS)
    ));
    assign('documentNumber', findByHeader(header =>
      this.headerContainsAnyKeyword(header, this.DOCUMENT_NUMBER_HEADER_KEYWORDS)
    ));

    return Object.keys(mappings).length > 0 ? mappings : null;
  }

  private normalizeHeader(header: string): string {
    return (header || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  }

  private hasWord(header: string, word: string): boolean {
    return header.split(' ').includes(word);
  }

  private headerContainsAnyKeyword(header: string, keywords: string[]): boolean {
    return keywords.some(keyword => {
      const normalizedKeyword = this.normalizeHeader(keyword);

      return normalizedKeyword.includes(' ')
        ? header.includes(normalizedKeyword)
        : this.hasWord(header, normalizedKeyword);
    });
  }
}
